import { StrictMode } from "react"
import { createRoot, type Root } from "react-dom/client"
import { ErrorBoundary } from "@/components/ErrorBoundary"
import { SeriesChart } from "@/components/chart/SeriesChart"
import { ChartThemeProvider, chartThemeName } from "@/lib/chart/ChartThemeContext"
import type { SeriesChartData } from "@/types/seriesChart"

const roots = new Map<Element, Root>()

function unmountAll(): void {
  roots.forEach((root) => root.unmount())
  roots.clear()
}

function mount(): void {
  const elements = document.querySelectorAll<HTMLElement>("[data-series-chart]")
  if (elements.length === 0) {
    unmountAll()
    return
  }

  const seen = new Set<Element>()
  elements.forEach((element) => {
    seen.add(element)
    const data = JSON.parse(element.dataset.seriesChart ?? "{}") as SeriesChartData
    let root = roots.get(element)
    if (!root) {
      root = createRoot(element)
      roots.set(element, root)
    }
    root.render(
      <StrictMode>
        <ErrorBoundary>
          <ChartThemeProvider theme={chartThemeName(element.dataset.chartTheme)}>
            <SeriesChart data={data} />
          </ChartThemeProvider>
        </ErrorBoundary>
      </StrictMode>,
    )
  })

  roots.forEach((root, element) => {
    if (!seen.has(element)) {
      root.unmount()
      roots.delete(element)
    }
  })
}

document.addEventListener("turbo:load", mount)
document.addEventListener("turbo:before-cache", unmountAll)
mount()
